import { useMemo } from 'react';
import { useDeferredMinecraftServers } from './useDeferredMinecraftServers';
import type { MinecraftServerStats } from '../types/statusMonitor';
import type { PopularityWindow, StarchartLiveStatus } from '../types/starchart';

/** Matches the Status Monitor cards: no frame for three minutes means the instance is dark. */
const OFFLINE_MS = 180_000;
const EXTENDED_DOWNTIME_MS = 900_000;

/**
 * The FQDN a live frame belongs to.
 *
 * The socket sends `fqdn` directly; frames from older agents only carry it nested under the
 * popularity block. Lowercased, because this is only ever used to join against StarChart history
 * and the PlayCDU server list.
 */
export const liveServerFqdn = (stats: MinecraftServerStats): string | null => {
  const fqdn = stats.fqdn || stats.popularity?.server_id;
  return fqdn ? fqdn.toLowerCase() : null;
};

const toLiveStatus = (stats: MinecraftServerStats, now: number): StarchartLiveStatus => {
  // `last_seen` is fractional unix seconds while `timestamp` on the same frame is milliseconds.
  const sinceLastSeen = now - stats.last_seen * 1000;

  // The frame carries all three ranking windows pre-computed, so a server page can show its
  // current standing with no extra request.
  const popularity: Partial<Record<PopularityWindow, number>> = {};
  if (typeof stats.popularity?.popularity_index === 'number') {
    popularity['24h'] = stats.popularity.popularity_index;
  }
  if (typeof stats.popularity_7d?.popularity_index === 'number') {
    popularity['7d'] = stats.popularity_7d.popularity_index;
  }
  if (typeof stats.popularity_28d?.popularity_index === 'number') {
    popularity['28d'] = stats.popularity_28d.popularity_index;
  }

  return {
    playerCount: stats.playerCount,
    maxPlayers: stats.maxPlayers,
    tps: stats.tps,
    mspt: stats.mspt,
    entities: stats.entities,
    chunks: stats.chunks,
    players: stats.players ?? [],
    motd: stats.motd,
    modloader: stats.modloader,
    modloaderVersion: stats.modloaderVersion,
    mcVersion: stats.mcVersion,
    quasarVersion: stats.quasarVersion,
    redisPing: stats.redisPing,
    hostname: stats.hostname,
    identifier: stats.identifier,
    clusterId: stats.clusterId,
    lastSeen: stats.last_seen,
    lastBackupTaken: stats.last_backup_taken ?? null,
    isOffline: sinceLastSeen > OFFLINE_MS,
    isExtendedDowntime: sinceLastSeen > EXTENDED_DOWNTIME_MS,
    popularity,
  };
};

/**
 * Live server state keyed by lowercased FQDN.
 *
 * One consumer per route. The socket replaces its whole map on every frame, so subscribing deep
 * in a table would re-render every row several times a second; views take this once and pass
 * primitives down to memoised children.
 */
export function useStarchartLive(enabled = true): Map<string, StarchartLiveStatus> {
  const servers = useDeferredMinecraftServers(enabled);

  return useMemo(() => {
    const now = Date.now();
    const map = new Map<string, StarchartLiveStatus>();

    for (const stats of Object.values(servers)) {
      const fqdn = liveServerFqdn(stats);
      if (!fqdn) continue;

      const status = toLiveStatus(stats, now);
      // A cluster can report several instances behind one FQDN; keep the one still reporting,
      // and among those the busiest, so a restarting sibling never masks a live one.
      const existing = map.get(fqdn);
      if (
        !existing ||
        (existing.isOffline && !status.isOffline) ||
        (existing.isOffline === status.isOffline && status.playerCount > existing.playerCount)
      ) {
        map.set(fqdn, status);
      }
    }

    return map;
  }, [servers]);
}
